/**
 * Shared types for the case IDE.
 *
 * An "artifact" is anything that can be opened in the center pane: a file from
 * the case filesystem, a draft, the assembled packet, or a USCIS form. Artifact
 * ids are stable strings so tabs, the chat's `data-open-artifact` parts and
 * `@`-mentions can all refer to the same thing.
 */

export type ArtifactKind =
  | "file"
  | "card"
  | "draft"
  | "packet"
  | "form"
  | "settings"
  | "plan"
  | "key_facts"
  | "time"
  | "company"
  | "workflows";

export const ARTIFACT_KINDS: readonly ArtifactKind[] = [
  "file",
  "card",
  "draft",
  "packet",
  "form",
  "settings",
  "plan",
  "key_facts",
  "time",
  "company",
  "workflows"
];

export type Artifact = {
  kind: ArtifactKind;
  /** Backend id: fs node id, draft_id, packet_id or filled-form id. */
  id: string;
  title: string;
  /** Optional page to scroll to on open (1-based). */
  page?: number | null;
};

/** Tab key shared by tabs, mentions and open-artifact requests. */
export const artifactKey = (a: Pick<Artifact, "kind" | "id">): string =>
  `${a.kind}:${a.id}`;

export const parseArtifactKey = (
  key: string
): Pick<Artifact, "kind" | "id"> | null => {
  const idx = key.indexOf(":");
  if (idx <= 0) return null;
  const kind = key.slice(0, idx) as ArtifactKind;
  if (!ARTIFACT_KINDS.includes(kind)) return null;
  return { kind, id: key.slice(idx + 1) };
};

/** What the chat learns about the editor with every turn. */
export type IdeSelection = {
  artifact: Artifact | null;
  /** Text the attorney has highlighted in the open artifact. */
  text: string | null;
  /** Filesystem nodes selected in the files tree. */
  selectedNodeIds: string[];
};

/** Case-scoped caches the backend can ask the IDE to refetch (`data-invalidate`). */
export type CaseInvalidateTarget =
  | "fs"
  | "drafts"
  | "packet"
  | "forms"
  | "research"
  | "case"
  | "autocategorize"
  | "plans"
  | "receipts"
  | "key_facts";

/**
 * Client-scoped caches the client manager's tools invalidate: the profile
 * (`client`), the client's cases, vault documents, intakes, reminders, the
 * history/dependent records and the activity feed. Routed to the client
 * profile page through `clientInvalidation.ts`.
 */
export type ClientInvalidateTarget =
  | "client"
  | "cases"
  | "documents"
  | "intakes"
  | "reminders"
  | "records"
  | "activity";

/**
 * Firm-scoped caches: the automation builder wrote or revised a firm skill
 * (`firm_skills`); an intake was saved as a firm template (`templates`).
 */
export type FirmInvalidateTarget = "firm_skills" | "templates";

/** The acting person's time entries (the assistant's time tools). */
export type TimeInvalidateTarget = "time";

/** A company's playbook (the assistant suggested a rule). */
export type CompanyInvalidateTarget = "playbook";

/** Targets the backend can ask the UI to refetch (`data-invalidate`). */
export type InvalidateTarget =
  | CaseInvalidateTarget
  | ClientInvalidateTarget
  | FirmInvalidateTarget
  | TimeInvalidateTarget
  | CompanyInvalidateTarget;

export type InvalidateListener = (targets: InvalidateTarget[]) => void;

/**
 * Agent mode. `agent` does the work; `plan` is read-only research that ends in
 * a plan document the attorney reviews and builds. Mirrors `AgentMode` on the
 * backend.
 */
export type AgentMode = "agent" | "plan";
export const AGENT_MODES: readonly AgentMode[] = ["agent", "plan"];

export type PlanTodoStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "cancelled";

export type PlanTodo = {
  id: string;
  content: string;
  status: PlanTodoStatus;
  /** Who does it: "CaseBlink" for the build, else the attorney or client. */
  owner?: string | null;
  /** `YYYY-MM-DD`, for work outside the build. */
  due?: string | null;
};

export type PlanStatus = "draft" | "building" | "complete";

/** Wire shape of a plan (`GET .../plans/:id`, `create_plan` tool output). */
export type CaseAgentPlan = {
  id: string;
  thread_id: string;
  individual_id: string;
  title: string;
  overview: string;
  markdown: string;
  todos: PlanTodo[];
  status: PlanStatus;
  /** Filed under the Work Products Plans folder ("Save to Work Products"). */
  saved_to_workspace?: boolean;
  created_at: string | null;
  updated_at: string | null;
};

/** One choice of an `ask_user` question. */
export type AskUserChoice = {
  value: string;
  label: string;
  description?: string;
  /** The agent's suggestion; at most one per question. */
  recommended?: boolean;
};

/**
 * One `ask_user` question, in the shape the backend normalizes to. Maps 1:1
 * onto a shadcn `Questionnaire` item.
 */
export type AskUserQuestion = {
  name: string;
  prompt: string;
  description?: string;
  choices: AskUserChoice[];
  multiple?: boolean;
  required?: boolean;
  /** Offer a freeform "another answer" input alongside the choices. */
  allow_other?: boolean;
  /** `date` answers with a date picker (`YYYY-MM-DD`). */
  kind?: "choice" | "date";
};

/** `ask_user` tool input. */
export type AskUserInput = {
  title?: string;
  questions: AskUserQuestion[];
};

/** A recorded answer: one value, several (multiple), or free text. */
export type AskUserAnswer = string | string[];
/** Answers by question name; `null` for a question the attorney skipped. */
export type AskUserAnswers = Record<string, AskUserAnswer | null>;

/** `ask_user` tool output, written by the client with `addToolOutput`. */
export type AskUserOutput = {
  answers: AskUserAnswers | null;
  skipped?: boolean;
  /** The firm's auto-answer went with the Recommended choices. */
  auto?: boolean;
};

/** Wire shape of `data-open-artifact`. Mirrors `ok(open=...)` on the backend. */
export type OpenArtifactData = {
  kind: ArtifactKind;
  id: string;
  title?: string;
  page?: number | null;
  /** Opened by a tool still running on it (a draft the builder is writing). */
  building?: boolean;
};

/**
 * A tool the running turn asks this browser to run against the draft open in
 * its editor (see `draftEditor/agentRelay.ts`); answered over HTTP.
 */
export type ClientToolRequestData = {
  id: string;
  tool: string;
  args: Record<string, unknown>;
  draftId: string;
};

/**
 * `contact_support`'s open: the note for CaseBlink Support. Nothing opens on
 * it; the call's own card shows the note and opens the Messenger on a click.
 */
export type SupportHandoffData = { kind: "support"; summary: string };

export type CaseIdeDataParts = {
  invalidate: { targets: InvalidateTarget[] };
  "open-artifact": OpenArtifactData | SupportHandoffData;
  "client-tool": ClientToolRequestData;
  /** The thread's mode changed server-side (an approved `request_mode_switch`). */
  mode: { mode: AgentMode };
};
